import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Search, Download, Trash2, X, ChevronLeft, ChevronRight, Truck, AlertTriangle } from "lucide-react";
import ModuleTabs from "@/components/ModuleTabs";

const PAGE_SIZE = 25;
function fmt(n) { return n === null || n === undefined ? "—" : Number(n).toLocaleString(); }
const todayIso = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

export default function TruckingRecords() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [shippingLine, setShippingLine] = useState("ALL");
  const [shippingLines, setShippingLines] = useState([]);
  const [dateFrom, setDateFrom] = useState(todayIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [confirmDel, setConfirmDel] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, kind = "ok") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    api.get("/settings/shipping-lines").then((r) => setShippingLines(r.data.values)).catch((e) => console.warn("shipping-lines fetch failed:", e?.message));
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (shippingLine !== "ALL") params.shipping_line = shippingLine;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/trucking", { params });
      setItems(r.data.items);
      setTotal(r.data.total);
    } catch {
      showToast("Failed to load records", "err");
    } finally {
      setLoading(false);
    }
  }, [skip, search, shippingLine, dateFrom, dateTo]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { setSkip(0); }, [search, shippingLine, dateFrom, dateTo]);

  const handleDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/trucking/${confirmDel.id}`);
      showToast("Trucking record deleted");
      setConfirmDel(null);
      fetchItems();
    } catch {
      showToast("Delete failed", "err");
    }
  };

  const exportCsv = async () => {
    try {
      const params = { skip: 0, limit: 2000 };
      if (search) params.search = search;
      if (shippingLine !== "ALL") params.shipping_line = shippingLine;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/trucking", { params });
      const rows = r.data.items;
      const header = ["Date", "Container", "Container 2", "Size", "Shipping Line", "From", "Status", "Condition", "Rate (USD)", "Shipment", "Truck Plate"];
      const csv = [header.join(","), ...rows.map((x) =>
        [x.date, x.container_no, x.container_no_2 || "", x.type_size, x.shipping_line, `"${(x.from_location || "").replace(/"/g, "")}"`, x.status, x.condition, x.rate_usd, x.shipment_ref || "", `"${(x.truck_plate || "").replace(/"/g, "")}"`].join(",")
      )].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `trucking-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      showToast(`Exported ${rows.length} records`);
    } catch {
      showToast("Export failed", "err");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;
  const hasFilters = search || shippingLine !== "ALL" || dateFrom || dateTo;

  return (
    <div className="space-y-6" data-testid="trucking-records-page">
      <ModuleTabs module="trucking" />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><Truck size={12} /> Trucking · Records</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-[#0a2952] tracking-tight leading-tight">All trucking trips.</h1>
          <p className="mt-2 text-slate-600">Per-container trips, billed in USD.</p>
        </div>
        <button type="button" data-testid="trucking-export-csv-btn" onClick={exportCsv}
          className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-[#0a2952] text-slate-700 px-4 py-2.5 rounded-md font-semibold transition-colors">
          <Download size={16} /> Export CSV
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="lg:col-span-2 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" data-testid="trucking-filter-search" placeholder="Search container no..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none" />
        </div>
        <select data-testid="trucking-filter-line" value={shippingLine} onChange={(e) => setShippingLine(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none bg-white">
          <option value="ALL">All Lines</option>
          {shippingLines.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <input type="date" data-testid="trucking-filter-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none" />
        <input type="date" data-testid="trucking-filter-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-[#0a2952] focus:ring-1 focus:ring-[#0a2952] outline-none" />
        {hasFilters && (
          <button type="button" data-testid="trucking-clear-filters-btn"
            onClick={() => { setSearch(""); setShippingLine("ALL"); setDateFrom(""); setDateTo(""); }}
            className="md:col-span-2 lg:col-span-5 inline-flex items-center gap-1 text-xs text-[#ff5b2c] font-semibold hover:underline justify-self-start">
            <X size={12} /> Clear filters
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="trucking-records-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-600">
              <tr>
                <Th>Date</Th>
                <Th>Container</Th>
                <Th>Size</Th>
                <Th>Line</Th>
                <Th>From</Th>
                <Th>Status</Th>
                <Th>Cond.</Th>
                <Th>Truck plate</Th>
                <Th align="right">Rate (USD)</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-500">Loading...</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-12 text-center" data-testid="trucking-records-empty">
                  <div className="flex flex-col items-center gap-2 text-slate-500">
                    <Truck size={32} className="text-slate-300" />
                    <span className="font-semibold">No trucking records match.</span>
                  </div>
                </td></tr>
              )}
              {!loading && items.map((row) => (
                <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`trucking-row-${row.id}`}>
                  <Td><span className="font-mono-tabular text-slate-700">{row.date}</span></Td>
                  <Td>
                    <span className="font-mono-tabular font-semibold text-[#0a2952]">{row.container_no}</span>
                    {row.container_no_2 && (
                      <div className="font-mono-tabular font-semibold text-[#0a2952] text-xs mt-0.5 flex items-center gap-1">
                        <span className="text-[#14b8a6]">+</span>{row.container_no_2}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <span className="inline-block px-2 py-0.5 text-xs font-bold tracking-wide bg-slate-100 text-[#0a2952] rounded-sm">{row.type_size}</span>
                    {row.container_no_2 && (
                      <span className="ml-1 inline-block px-1.5 py-0.5 text-[10px] font-bold tracking-wide bg-[#14b8a6]/15 text-[#0f766e] rounded-sm">×2</span>
                    )}
                  </Td>
                  <Td><span className="text-xs font-semibold text-[#0a2952]">{row.shipping_line}</span></Td>
                  <Td><span className="text-xs text-slate-600">{row.from_location || "—"}</span></Td>
                  <Td><span className={`inline-block px-2 py-0.5 text-xs font-bold tracking-wide rounded-sm ${row.status === "EMPTY" ? "bg-amber-50 text-amber-700" : "bg-[#22c55e]/10 text-[#15803d]"}`}>{row.status}</span></Td>
                  <Td><span className={`inline-block px-2 py-0.5 text-xs font-bold tracking-wide rounded-sm ${row.condition === "SOUND" ? "bg-[#22c55e]/10 text-[#15803d]" : "bg-red-50 text-red-700"}`}>{row.condition}</span></Td>
                  <Td>
                    {row.truck_plate ? (
                      <span className="font-mono-tabular text-xs text-slate-700">{row.truck_plate}</span>
                    ) : (
                      <span className="text-xs text-slate-300">—</span>
                    )}
                    {row.shipment_ref && (
                      <div className="text-[10px] text-slate-400 font-mono-tabular">#{row.shipment_ref}</div>
                    )}
                  </Td>
                  <Td align="right"><span className="font-mono-tabular font-semibold text-[#0a2952]">{fmt(row.rate_usd)}</span></Td>
                  <Td align="right">
                    <button type="button" data-testid={`trucking-delete-btn-${row.id}`} onClick={() => setConfirmDel(row)}
                      className="w-8 h-8 grid place-items-center rounded text-slate-500 hover:bg-red-50 hover:text-red-600">
                      <Trash2 size={14} />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
          <div className="text-xs text-slate-600 font-mono-tabular">
            {total === 0 ? "0 records" : `${skip + 1}–${Math.min(skip + PAGE_SIZE, total)} of ${fmt(total)}`}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="trucking-prev-page-btn" onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} disabled={skip === 0}
              className="w-8 h-8 grid place-items-center rounded border border-slate-300 hover:border-[#0a2952] disabled:opacity-40 disabled:cursor-not-allowed">
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs font-semibold text-slate-700 px-2 font-mono-tabular">{currentPage} / {totalPages}</span>
            <button type="button" data-testid="trucking-next-page-btn" onClick={() => setSkip(Math.min((totalPages - 1) * PAGE_SIZE, skip + PAGE_SIZE))} disabled={currentPage >= totalPages}
              className="w-8 h-8 grid place-items-center rounded border border-slate-300 hover:border-[#0a2952] disabled:opacity-40 disabled:cursor-not-allowed">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {confirmDel && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="trucking-confirm-dialog">
          <div className="bg-white rounded-md w-full max-w-md border border-slate-200 shadow-2xl">
            <div className="p-6">
              <div className="w-12 h-12 rounded-full bg-red-50 grid place-items-center mb-4"><AlertTriangle className="text-red-600" /></div>
              <div className="font-display font-extrabold text-xl text-[#0a2952] mb-2">Delete trucking record?</div>
              <p className="text-sm text-slate-600">
                Permanently remove <span className="font-mono-tabular font-bold text-[#0a2952]">{confirmDel.container_no}</span> from {confirmDel.date}.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
              <button type="button" onClick={() => setConfirmDel(null)} data-testid="trucking-confirm-cancel-btn" className="px-4 py-2 rounded border border-slate-300 text-sm font-semibold">Cancel</button>
              <button type="button" onClick={handleDelete} data-testid="trucking-confirm-delete-btn" className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div data-testid="toast" className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-md shadow-lg text-sm font-semibold ${
          toast.kind === "err" ? "bg-red-600 text-white" : "bg-[#0a2952] text-white"
        }`}>{toast.msg}</div>
      )}
    </div>
  );
}

function Th({ children, align = "left" }) { return <th className={`px-4 py-3 text-${align} whitespace-nowrap`}>{children}</th>; }
function Td({ children, align = "left" }) { return <td className={`px-4 py-3 text-${align} whitespace-nowrap`}>{children}</td>; }
